import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { friendlyError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [shopId, setShopId] = useState(() => localStorage.getItem("reto_shop") || null);
  const [shopName, setShopName] = useState(() => localStorage.getItem("reto_shop_name") || null);

  const chooseShop = useCallback((id, name) => {
    localStorage.setItem("reto_shop", id);
    setShopId(id);
    if (name) { localStorage.setItem("reto_shop_name", name); setShopName(name); }
  }, []);

  // Work out who we are from the session cookie.
  const refreshMe = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      // Attach the person to their shop (staff/shop owner carry one).
      if (me.data.shopId) {
        try {
          const shops = await api.get("/shops");
          const own = shops.data.find((s) => s.id === me.data.shopId) || shops.data[0];
          if (own) chooseShop(own.id, own.name);
          else chooseShop(me.data.shopId);
        } catch { chooseShop(me.data.shopId); }
      } else {
        try {
          const shops = await api.get("/shops");
          if (shops.data.length === 1) chooseShop(shops.data[0].id, shops.data[0].name);
        } catch {}
      }
      return me.data;
    } catch {
      setUser(null);
      return null;
    }
  }, [chooseShop]);

  useEffect(() => { refreshMe().finally(() => setReady(true)); }, [refreshMe]);

  const requestLink = useCallback(async (email) => {
    try { const r = await api.post("/auth/request-link", { email }); return { ok: true, ...r.data }; }
    catch (e) { return { ok: false, message: friendlyError(e) }; }
  }, []);

  const verifyCode = useCallback(async (email, code) => {
    try { await api.post("/auth/verify-code", { email, code }); await refreshMe(); return { ok: true }; }
    catch (e) { return { ok: false, message: friendlyError(e) }; }
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    try { await api.post("/auth/login", { email, password }); await refreshMe(); return { ok: true }; }
    catch (e) { return { ok: false, message: friendlyError(e) }; }
  }, [refreshMe]);

  const staffPin = useCallback(async (shopIdV, staffId, pin) => {
    try { await api.post("/auth/staff-pin", { shopId: shopIdV, staffId, pin }); await refreshMe(); return { ok: true }; }
    catch (e) { return { ok: false, message: friendlyError(e) }; }
  }, [refreshMe]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout", {}); } catch {}
    localStorage.removeItem("reto_shop"); localStorage.removeItem("reto_shop_name");
    setUser(null); setShopId(null); setShopName(null);
  }, []);

  const value = { user, ready, shopId, shopName, chooseShop, requestLink, verifyCode, login, staffPin, logout, refreshMe, can: user?.can || {} };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
